import { useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from "@/components/ui";
import { cn, formatCurrency } from "@/lib/utils";
import type { AttributeOption, Product, ProductVariant } from "@/types";

interface ProductCustomizationDialogProps {
  product: Product;
  onClose: () => void;
  onConfirm: (
    variant: ProductVariant,
    options: AttributeOption[],
    quantity: number,
  ) => void;
  currencySymbol: string;
}

export function ProductCustomizationDialog({
  product,
  onClose,
  onConfirm,
  currencySymbol,
}: ProductCustomizationDialogProps) {
  const defaultOptionIds = useMemo(() => {
    const ids = new Set<string>();

    for (const attr of product.attributes ?? []) {
      const defaults = attr.options.filter((o) => o.isDefault);

      if (attr.selectionType === "single") {
        const first = defaults[0] ?? null;

        if (first) {
          ids.add(first.id);
        }
      } else {
        for (const opt of defaults) {
          ids.add(opt.id);
        }
      }
    }

    return ids;
  }, [product]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    product.variants?.find((v) => v.isDefault)?.id ||
      product.variants?.[0]?.id ||
      "",
  );
  const [selectedOptionIds, setSelectedOptionIds] =
    useState<Set<string>>(defaultOptionIds);
  const [quantity, setQuantity] = useState(1);

  const selectedVariant = product.variants?.find(
    (v) => v.id === selectedVariantId,
  );
  const selectedOptions =
    product.attributes?.flatMap((attr) =>
      attr.options.filter((opt) => selectedOptionIds.has(opt.id)),
    ) || [];

  const basePrice = selectedVariant?.price || 0;
  const optionsPrice = selectedOptions.reduce(
    (sum, opt) => sum + opt.priceAdjustment,
    0,
  );
  const unitPrice = basePrice + optionsPrice;
  const totalPrice = unitPrice * quantity;

  const missingRequired = (product.attributes ?? []).filter((attr) => {
    if (!attr.isRequired) {
      return false;
    }

    return !attr.options.some((opt) => selectedOptionIds.has(opt.id));
  });

  const canSubmit = Boolean(selectedVariant) && missingRequired.length === 0;

  const handleOptionToggle = (
    optionId: string,
    attribute: Product["attributes"][0],
  ) => {
    const newSelected = new Set(selectedOptionIds);
    const already = newSelected.has(optionId);

    if (attribute.selectionType === "single") {
      for (const opt of attribute.options) {
        newSelected.delete(opt.id);
      }

      if (!already) {
        newSelected.add(optionId);
      }
    } else if (already) {
      newSelected.delete(optionId);
    } else {
      newSelected.add(optionId);
    }

    setSelectedOptionIds(newSelected);
  };

  const handleConfirm = () => {
    if (!canSubmit || !selectedVariant) {
      return;
    }

    onConfirm(selectedVariant, selectedOptions, quantity);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="text-xl">{product.name}</DialogTitle>
          {product.description && (
            <p className="text-sm text-muted-foreground">
              {product.description}
            </p>
          )}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {product.variants && product.variants.length > 1 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Size</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {product.variants.map((variant) => {
                    const active = selectedVariantId === variant.id;

                    return (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={() => setSelectedVariantId(variant.id)}
                        className={cn(
                          "flex flex-col items-start rounded-md border px-3 py-2 text-left transition-colors",
                          active
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "hover:border-primary/40 hover:bg-muted",
                        )}
                      >
                        <span className="text-sm font-medium">
                          {variant.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatCurrency(variant.price, currencySymbol)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.attributes?.map((attribute) => {
              const isMissing = missingRequired.some(
                (a) => a.id === attribute.id,
              );

              return (
                <div key={attribute.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">
                      {attribute.name}
                      {attribute.isRequired && (
                        <span className="ml-1 text-destructive">*</span>
                      )}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {attribute.selectionType === "single"
                        ? "Pick one"
                        : "Pick any"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {attribute.options.map((option) => {
                      const checked = selectedOptionIds.has(option.id);

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() =>
                            handleOptionToggle(option.id, attribute)
                          }
                          className={cn(
                            "flex w-full items-center justify-between rounded-md border p-3 text-left transition-colors",
                            checked
                              ? "border-primary bg-primary/5"
                              : "hover:border-primary/30 hover:bg-muted",
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={checked}
                              className="pointer-events-none"
                            />
                            <span className="text-sm">{option.name}</span>
                          </div>
                          {option.priceAdjustment !== 0 && (
                            <span className="text-xs text-muted-foreground">
                              {option.priceAdjustment > 0 ? "+" : ""}
                              {formatCurrency(
                                option.priceAdjustment,
                                currencySymbol,
                              )}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {isMissing && (
                    <p className="text-xs text-destructive">
                      Please select an option.
                    </p>
                  )}
                </div>
              );
            })}

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Quantity</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center text-lg font-semibold">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="shrink-0 flex-row items-center gap-3 border-t bg-muted/30 px-6 py-4 sm:justify-between">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Total
            </span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(totalPrice, currencySymbol)}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!canSubmit}>
              Add to Cart
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
